import {Controller} from "@luciad/ria/view/controller/Controller";
import {FlickerController} from "@luciad/ria/view/controller/FlickerController";
import {SwipeController} from "@luciad/ria/view/controller/SwipeController";
import {Map} from "@luciad/ria/view/Map";
import React, {PropsWithChildren, useEffect, useRef, useState} from "react";
import {CompositeController} from "@luciad/ria/view/controller/CompositeController";
import {useFullScreen} from "../../hooks/useFullScreen";
import {FlickerUI} from "../controllers/FlickerUI";
import {SwipeUI} from "../controllers/SwipeUI";
import "./TopLeftButtons.css";

interface Props {
  map: Map;
  swipeFlicker?: boolean;
}

export const TopLeftButtons = ({map, swipeFlicker = false, children}: PropsWithChildren<Props>) => {
  const {fullScreen, toggleFullScreen} = useFullScreen();
  const [infoPanelOpen, setInfoPanelOpen] = useState(false);
  const [flickerController, setFlickerController] = useState<FlickerController | null>(null);
  const [swipeController, setSwipeController] = useState<SwipeController | null>(null);
  const infoPanelContentRef = useRef<HTMLDivElement | null>(null);

  const toggleInfoPanelOpen = () => {
    setInfoPanelOpen(() => !infoPanelOpen);
  }

  useEffect(() => {
    // The info panel contains a description of the sample and how to use it.
    // It is not a React component but is a static snippet of HTML in the application's index.html.
    const infoPanel = document.getElementById("infopanel");
    if (infoPanel && infoPanelContentRef.current) {
      infoPanelContentRef.current.innerHTML = infoPanel.innerHTML;
    }
  }, [])

  async function enableFlickerController() {
    if(flickerController){
      return
    }
    const newFlickerController = new FlickerController();
    // make sure the controller that was active on the map, still works while the flicker controller is active
    let newMapController: Controller;
    if (map.controller) {
      const compositeController = new CompositeController();
      compositeController.appendController(newFlickerController);
      compositeController.appendController(map.controller);
      newMapController = compositeController;
    } else {
      newMapController = newFlickerController;
    }
    map.controller = newMapController;
    newMapController.on("Deactivated", () => setFlickerController(null));
    setFlickerController(newFlickerController);
  }

  async function enableSwipeController() {
    if (swipeController){
      return
    }
    const newSwipeController = new SwipeController();
    // make sure the controller that was active on the map, still works while the swipe controller is active
    let newMapController: Controller;
    if (map.controller) {
      const compositeController = new CompositeController();
      compositeController.appendController(newSwipeController);
      compositeController.appendController(map.controller);
      newMapController = compositeController;
    } else {
      newMapController = newSwipeController;
    }
    map.controller = newMapController;
    newMapController.on("Deactivated", () => setSwipeController(null));
    setSwipeController(newSwipeController);
  }

  return (
      <>
        <div className="top-left-buttons">
          <a role="button" href="https://hexagon.com/" target="_blank" title="Homepage">
            <span className="lcd-icon lcd-icon-logo-hexagon-bug"/>
          </a>
          <a className={infoPanelOpen ? "active" : ""} role="button" title="More info" onClick={toggleInfoPanelOpen}>
            <span className="lcd-icon lcd-icon-info"/>
          </a>
          <a className={fullScreen ? "active" : ""} role="button" title="Fullscreen" onClick={toggleFullScreen}>
            <span className="lcd-icon lcd-icon-fit"/>
          </a>
          {swipeFlicker && <a className={swipeController ? "active" : ""} role="button" title="Swipe between layers">
            <span className="lcd-icon lcd-icon-arrows-expand" onClick={enableSwipeController}/>
          </a>}
          {swipeFlicker &&
           <a className={flickerController ? "active" : ""} role="button" title="Flicker between layers">
             <span className="lcd-icon lcd-icon-arrow-left-right" onClick={enableFlickerController}/>
           </a>}
          {children}
          <div className={"info-panel" + (infoPanelOpen ? " open" : "")}>
            <div ref={infoPanelContentRef} className="info-panel-content"/>
          </div>
        </div>
        {swipeFlicker && flickerController && <FlickerUI map={map} controller={flickerController}/>}
        {swipeFlicker && swipeController && <SwipeUI map={map} controller={swipeController}/>}
      </>
  )
};